import React, {
  FC,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  Box,
  Center,
  HStack,
  Stack,
  Collapse,
  chakra,
  shouldForwardProp,
} from '@chakra-ui/react';
import { isValidMotionProp, motion } from 'framer-motion';
import { map } from 'lodash';
import { customTheme } from 'theme';

export interface GettingStartedListStylesProps {
  buttonPaddingLeft: string | number;
  stepWidth?: string | number;
  lineLeftPadding?: string | number;
  listPaddingTop?: string | number;
  listPaddingBottom?: string | number;
}

interface Props {
  steps: {
    label: string;
    component?: ReactNode;
    header?: string;
    key: string;
    onClick?();
  }[];

  styles?: GettingStartedListStylesProps;
}

export const GettingStartedList: FC<Props> = props => {
  const { steps, styles } = props;
  const [hoveredItem, setHoveredItem] = useState<Record<string, any>>({});
  const [previewOffset, setPreviewOffset] = useState(0);
  const previewOffsetRef = useRef(0);
  const previewRef = useRef<HTMLDivElement>(null);

  const updatePreviewPosition = useCallback(() => {
    if (!previewRef.current) return;

    const viewportPadding = 16;
    const previewBounds = previewRef.current.getBoundingClientRect();
    const naturalTop = previewBounds.top - previewOffsetRef.current;
    const naturalBottom = previewBounds.bottom - previewOffsetRef.current + 15;
    const maximumTop = window.innerHeight - viewportPadding;

    let nextOffset = Math.min(0, maximumTop - naturalBottom);
    nextOffset = Math.max(nextOffset, viewportPadding - naturalTop);

    if (nextOffset !== previewOffsetRef.current) {
      previewOffsetRef.current = nextOffset;
      setPreviewOffset(nextOffset);
    }
  }, []);

  useLayoutEffect(() => {
    if (!hoveredItem?.component) return;

    updatePreviewPosition();

    const resizeObserver = new ResizeObserver(updatePreviewPosition);
    if (previewRef.current) resizeObserver.observe(previewRef.current);

    window.addEventListener('resize', updatePreviewPosition);
    window.addEventListener('scroll', updatePreviewPosition, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePreviewPosition);
      window.removeEventListener('scroll', updatePreviewPosition, true);
    };
  }, [hoveredItem, updatePreviewPosition]);

  const AnimatedButton = ({ item }) => {
    return (
      <Box pl={styles?.buttonPaddingLeft ?? 14}>
        <Center
          border="1px solid"
          borderRadius={8}
          borderColor={customTheme.colors.gray['100']}
          px={4}
          bg={customTheme.colors.gettingStartedItemBg}
          color="white"
          fontSize="lg"
          w={styles?.stepWidth ?? '500px'}
          borderWidth="1px"
          p={3}
          justifyContent="flex-start"
          transform="scale(1)"
          transition="all 0.3s ease-in-out"
          _hover={{ transform: 'scale(1.1)', py: 5 }}
          onMouseEnter={() => {
            previewOffsetRef.current = 0;
            setPreviewOffset(0);
            setHoveredItem(item);
          }}
          cursor={!!item?.onClick ? 'pointer' : 'default'}
          onClick={item?.onClick}
          overflow="hidden"
          onMouseOut={() => setHoveredItem({})}
        >
          {item.label}
        </Center>
      </Box>
    );
  };

  // Create a motion-enabled Box
  const MotionBox = chakra(motion.div, {
    shouldForwardProp: prop =>
      isValidMotionProp(prop) || shouldForwardProp(prop),
  });

  const AnimatedLine = () => {
    return (
      <MotionBox
        height="1px"
        bg={customTheme.colors.gray['100']}
        initial={{ width: 0 }}
        animate={{ width: '100%' }}
        transition={{ duration: '1' }}
      />
    );
  };

  return (
    <Stack
      spacing={10}
      w="full"
      pt={styles?.listPaddingTop}
      pb={styles?.listPaddingBottom}
    >
      {map(steps, (o, index) => (
        <HStack w="full" spacing={0} key={'getting-started-' + index}>
          <HStack spacing={0}>
            <AnimatedButton key={index} item={o} />
            {hoveredItem &&
              hoveredItem?.key === o?.key &&
              !!hoveredItem?.component && (
                <Box pl={styles?.lineLeftPadding ?? 6} w={80}>
                  <AnimatedLine />
                </Box>
              )}
          </HStack>
          {/* Right Side Info Box */}
          <Collapse
            in={!!hoveredItem}
            transition={{ enter: { duration: 0.5 } }}
          >
            {hoveredItem &&
              hoveredItem?.key === o?.key &&
              !!hoveredItem?.component && (
                <HStack spacing={0} align="flex-start" overflow="hidden">
                  <Box bg="white" overflow="hidden">
                    <Box mt={-10}>
                      <Box
                        ref={previewRef}
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        w="520px"
                        minH="150px"
                        bg={customTheme.colors.gettingStartedItemBg}
                        boxShadow="lg"
                        position="absolute"
                        transform={`translateY(${previewOffset}px)`}
                        //left="961px"
                        borderColor={customTheme.colors.gray['100']}
                      >
                        <Stack spacing={6} px={2}>
                          <Center
                            fontSize="2xl"
                            fontWeight="bold"
                            color="primary"
                          >
                            {hoveredItem?.header}
                          </Center>
                          <Box fontSize="lg" px={4}>
                            {hoveredItem?.component}
                          </Box>
                        </Stack>
                      </Box>
                    </Box>
                  </Box>
                </HStack>
              )}
          </Collapse>
        </HStack>
      ))}
    </Stack>
  );
};
